import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Loader2, AlertCircle, RefreshCw, Sparkles, Eye,
  ClipboardList, CalendarClock, MessageCircle, FileText, ChevronDown, X, Home as HomeIcon, Check,
  Upload, FileVideo, Copy, Link2, Calculator, History, Download, ShieldCheck, HelpCircle,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { api, uploadFile, mediaUrl } from "@/lib/api";
import { Events } from "@/lib/analytics";
import { useSite } from "@/context/SiteContext";
import { toast } from "sonner";
import {
  NEEDS, PROPERTY_TYPES, CONDITION_STOPS, AREA_CONDITIONS, WINDOW_COMPLEXITY,
  DEADLINE_PRESETS, DETAIL_LEVELS, AREAS, PREP_NOTES,
} from "@/property-intelligence/data";
import {
  needByKey, levelByKey, conditionLabel, visibleAreas, defaultAddonsFor,
  buildPlan, buildPrefill, buildWhatsAppText,
} from "@/property-intelligence/engine";
import {
  Stepper, ChoiceCard, SegmentedLevels, PriceRange, DurationBand, ComplexityBadge, Chip, kicker, cardCls,
} from "@/property-intelligence/controls";
import { PropertyBlueprint } from "@/property-intelligence/PropertyBlueprint";
import { AiPhotoAnalysisPanel } from "@/property-intelligence/AiPhotoAnalysisPanel";
import { RoomBreakdown, TimelinePhases } from "@/property-intelligence/PlanOutput";
import { PlanPdfDocument, downloadPlanPdf } from "@/property-intelligence/PlanPdf";

const inputCls = "w-full rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]";
const FLOW = ["intro", "basics", "need-condition", "areas", "add-ons", "summary", "ready"];
const CONFIG_STEPS = ["basics", "need-condition", "areas", "add-ons", "summary"];
const STEP_TITLES = {
  basics: "Property basics",
  "need-condition": "Your need & condition",
  areas: "Areas & scope",
  "add-ons": "Tailor your plan",
  summary: "Your Property Profile",
};

const initialState = () => {
  const base = {
    needKey: "detail", propertyType: "Apartment", city: "", sqm: 90, rooms: 3, bathrooms: 1,
    furnishing: "Empty", conditionKey: "Moderate", kitchenCondition: "Moderate", bathroomCondition: "Moderate",
    windowComplexity: "Standard", balcony: false, cabinets: true, appliances: true, constructionResidue: false,
    deadlinePreset: "Flexible", deadlineDate: "", detailLevel: "ascendia", addons: [], priorityAreas: "",
  };
  base.addons = defaultAddonsFor("ascendia", base);
  return base;
};

export default function PropertyIntelligence() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const site = useSite();
  const reduce = useReducedMotion();
  const [cfg, setCfg] = useState(null);
  const [loadState, setLoadState] = useState("loading"); // loading | ok | error
  const [stage, setStage] = useState("intro");
  const [s, setS] = useState(initialState);
  const [activeArea, setActiveArea] = useState(null);
  const [openArea, setOpenArea] = useState(null);
  const [showMicro, setShowMicro] = useState(false);
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedLink, setSavedLink] = useState(null);
  const [aiSummary, setAiSummary] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const pdfRef = React.useRef(null);
  const hydratedRef = React.useRef(false);

  const loadCfg = useCallback(() => {
    setLoadState("loading");
    api.get("/estimator-config")
      .then(({ data }) => { setCfg(data); setLoadState("ok"); })
      .catch(() => setLoadState("error"));
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  // Hydrate from a saved plan link (?plan=token) so visitors can return / share.
  useEffect(() => {
    const token = params.get("plan");
    if (!token || hydratedRef.current) return;
    hydratedRef.current = true;
    api.get(`/plans/${token}`)
      .then(({ data }) => {
        if (data?.selections) setS((p) => ({ ...p, ...data.selections, planToken: token }));
        if (Array.isArray(data?.media)) setMedia(data.media.map((m) => ({ ...m, preview: mediaUrl(m.url) })));
        setStage("summary");
        toast.success("Your saved plan is ready");
      })
      .catch(() => toast.error("That saved plan link could not be found"));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per ?plan token change; api/refs are stable
  }, [params]);

  // Prefill from a saved client property (?property=&city=&sqm=&type=&beds=&baths=) and jump straight to basics.
  const prefilledRef = React.useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    if (params.get("plan")) return; // saved-plan hydration takes precedence
    const city = params.get("city");
    const sqm = params.get("sqm");
    const type = params.get("type");
    const beds = params.get("beds");
    const baths = params.get("baths");
    if (!(city || sqm || type || beds || baths)) return;
    prefilledRef.current = true;
    setS((p) => ({
      ...p,
      ...(type ? { propertyType: type } : {}),
      ...(city ? { city } : {}),
      ...(sqm && !Number.isNaN(Number(sqm)) ? { sqm: Number(sqm) } : {}),
      ...(beds && !Number.isNaN(Number(beds)) ? { rooms: Number(beds) } : {}),
      ...(baths && !Number.isNaN(Number(baths)) ? { bathrooms: Number(baths) } : {}),
    }));
    setStage("basics");
    const nickname = params.get("property");
    toast.success(nickname ? `Prefilled from “${nickname}”` : "Property details prefilled");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // ---------- Save & Return (anonymous, localStorage only, 7-day expiry) ----------
  const PROGRESS_KEY = "ascendia_pi_progress";
  const PROGRESS_TTL = 7 * 24 * 60 * 60 * 1000;
  const [resume, setResume] = useState(null);
  const startedRef = React.useRef(false);
  const completedRef = React.useRef(false);

  // Offer to resume an unfinished plan on a fresh visit (not when opening a shared plan link).
  useEffect(() => {
    if (params.get("plan")) return;
    try {
      const saved = JSON.parse(localStorage.getItem(PROGRESS_KEY) || "null");
      if (!saved || (Date.now() - (saved.savedAt || 0)) > PROGRESS_TTL) { localStorage.removeItem(PROGRESS_KEY); return; }
      if (saved.stage && saved.stage !== "intro" && saved.s && Number(saved.s.sqm) > 0) setResume(saved);
    } catch (e) { console.debug("PI resume check failed", e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave in-progress plan to this browser only (privacy-safe by design, per spec #8).
  // Stores ONLY the visitor's chosen property attributes (size, rooms, condition, etc.) —
  // never any contact PII (name/phone/email/address are collected later on the Contact form)
  // and never any auth token. Anonymous, device-local, and expires after 7 days.
  useEffect(() => {
    if (stage === "intro" || stage === "ready") return;
    if (!(Number(s.sqm) > 0)) return;
    try {
      localStorage.setItem(PROGRESS_KEY, JSON.stringify({
        stage, s,
        media: media.map(({ path, url, original_filename, content_type }) => ({ path, url, original_filename, content_type })),
        savedAt: Date.now(),
      }));
    } catch (e) { console.debug("PI autosave skipped (storage unavailable):", e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts to [stage, s, media]; PROGRESS_KEY/TTL are stable module-scope constants
  }, [stage, s, media]);

  const continuePlan = () => {
    if (!resume) return;
    setS((p) => ({ ...p, ...resume.s }));
    if (Array.isArray(resume.media)) setMedia(resume.media.map((m) => ({ ...m, preview: mediaUrl(m.url) })));
    goTo(resume.stage || "summary");
    setResume(null);
  };
  const discardResume = () => { try { localStorage.removeItem(PROGRESS_KEY); } catch (e) { console.debug("PI clear progress failed:", e); } setResume(null); };

  // Funnel analytics: plan started (first move off intro) and completed (reaching the plan).
  useEffect(() => {
    if (stage !== "intro" && !startedRef.current) {
      startedRef.current = true;
      Events.planStarted({ entry: params.get("intent") || "direct" });
    }
    if (stage === "ready" && !completedRef.current) {
      completedRef.current = true;
      Events.planCompleted({
        service: needByKey(s.needKey).estimatorKey, detail_level: s.detailLevel, condition: s.conditionKey,
        sqm: Number(s.sqm) || undefined, estimate_low: plan?.est?.low, estimate_high: plan?.est?.high, photos: media.length,
      });
      try { localStorage.removeItem(PROGRESS_KEY); } catch (e) { console.debug("PI clear progress on complete failed:", e); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Derived selections (service key + visible areas + photos) for the pricing engine.
  const sd = useMemo(() => ({
    ...s,
    estimatorKey: needByKey(s.needKey).estimatorKey,
    areas: visibleAreas(s).map((a) => a.id),
    photoCount: media.length,
    mediaList: media,
  }), [s, media]);
  const plan = useMemo(() => buildPlan(cfg, sd), [cfg, sd]);
  const areas = useMemo(() => visibleAreas(s), [s]);

  const set = (patch) => setS((p) => ({ ...p, ...patch }));
  const setDetail = (lvl) => setS((p) => ({ ...p, detailLevel: lvl, addons: defaultAddonsFor(lvl, p) }));
  const toggleAddon = (id) => setS((p) => ({ ...p, addons: p.addons.includes(id) ? p.addons.filter((x) => x !== id) : [...p.addons, id] }));

  const idx = FLOW.indexOf(stage);
  const goTo = (st) => { setStage(st); window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" }); };
  const next = () => {
    if (stage === "basics" && (!s.sqm || Number(s.sqm) <= 0)) { toast.error("To tailor the plan, add an approximate size (m²)."); return; }
    goTo(FLOW[Math.min(FLOW.length - 1, idx + 1)]);
  };
  const back = () => goTo(FLOW[Math.max(0, idx - 1)]);

  const stashPrefill = () => {
    try { sessionStorage.setItem("ascendia_prefill", JSON.stringify(buildPrefill(sd, plan).prefill)); } catch (e) { console.error("prefill stash failed", e); }
  };
  const getFinalQuote = () => { stashPrefill(); nav("/contact?prefill=intelligence"); };
  const askPlan = () => { stashPrefill(); nav("/ask?intent=plan"); };
  const checkAvailability = () => { stashPrefill(); nav("/request-time"); };
  const seeEstimate = () => { stashPrefill(); nav("/estimate"); };
  const waHref = `https://wa.me/${site?.phone_intl || "972587790553"}?text=${encodeURIComponent(buildWhatsAppText(sd, plan))}`;

  // Photo Boost — optional uploads (reuses existing /api/upload). 2+ photos tighten the estimate band.
  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try { const res = await uploadFile(file); setMedia((prev) => [...prev, { ...res, preview: mediaUrl(res.url) }]); }
      catch { toast.error(`Couldn't upload ${file.name}`); }
    }
    setUploading(false); e.target.value = "";
  };
  const removeMedia = (path) => setMedia((prev) => prev.filter((m) => m.path !== path));

  // Apply AI vision suggestions to the wizard selections (safe subset: condition/service/size).
  const applyAi = (patch, agg) => {
    const clean = {};
    if (patch.conditionKey) clean.conditionKey = patch.conditionKey;
    if (patch.needKey) clean.needKey = patch.needKey;
    if (Number(patch.sqm) > 0) clean.sqm = Number(patch.sqm);
    if (Object.keys(clean).length) setS((p) => ({ ...p, ...clean }));
    if (agg?.summary) setAiSummary(agg.summary);
    try { Events?.piAiApplied?.(clean); } catch (e) { /* analytics optional */ }
  };

  // Download the branded PDF property plan (client-side capture).
  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await downloadPlanPdf(pdfRef.current, `Ascendia-Property-Plan-${(s.city || "home").replace(/\s+/g, "-")}.pdf`);
      toast.success("Your property plan PDF is downloading");
    } catch (e) {
      console.error("PDF export failed:", e);
      toast.error("Could not generate the PDF. Please try again.");
    } finally { setDownloadingPdf(false); }
  };

  // Save My Plan — persist selections + estimate + photos, return a private shareable link.
  const savePlan = async () => {
    setSaving(true);
    try {
      const body = {
        selections: s,
        estimate: plan?.est || null,
        media: media.map(({ path, url, original_filename, content_type }) => ({ path, url, original_filename, content_type })),
        meta: { need: needByKey(s.needKey).label, level: levelByKey(s.detailLevel).label },
      };
      const { data } = await api.post("/plans", body);
      const link = `${window.location.origin}/property-intelligence?plan=${data.token}`;
      setSavedLink(link);
      setS((p) => ({ ...p, planToken: data.token }));
      toast.success("Plan saved — your private link is ready");
    } catch { toast.error("Could not save your plan. Please try again."); }
    finally { setSaving(false); }
  };
  const copyLink = () => { navigator.clipboard?.writeText(savedLink); toast.success("Link copied"); };
  const shareWa = savedLink ? `https://wa.me/?text=${encodeURIComponent(`My Ascendia Property Plan: ${savedLink}`)}` : "#";

  const stepNumber = CONFIG_STEPS.indexOf(stage) + 1;
  const showProgress = CONFIG_STEPS.includes(stage);
  const showProfilePanel = ["basics", "need-condition", "areas", "add-ons", "summary"].includes(stage);

  // ---------- top chrome ----------
  const TopBar = () => (
    <header className="sticky top-0 z-40 border-b border-[color:var(--border-neutral)] bg-[hsl(var(--background))]/85 backdrop-blur">
      <div className="container-ascendia flex h-14 items-center justify-between">
        <button onClick={() => nav("/")} className="flex items-center gap-2 text-white" data-testid="pi-home-link">
          <Sparkles className="h-4 w-4 text-gold-soft" />
          <span className="font-display text-sm font-semibold tracking-wide">Ascendia <span className="text-gold">Property Intelligence</span></span>
        </button>
        <div className="flex items-center gap-3 sm:gap-4">
          <a href="/trust" target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex items-center gap-1 text-xs text-muted-c hover:text-gold-soft" data-testid="pi-trust-link"><ShieldCheck className="h-3.5 w-3.5" /> Trust Center</a>
          <a href="/faq" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-muted-c hover:text-gold-soft" data-testid="pi-faq-link"><HelpCircle className="h-3.5 w-3.5" /> Help / FAQ</a>
          <span className="hidden sm:block h-4 w-px bg-white/15" />
          <button onClick={() => nav("/")} className="inline-flex items-center gap-1 text-xs text-muted-c hover:text-white" data-testid="pi-exit-button"><X className="h-4 w-4" /> Exit</button>
        </div>
      </div>
      {showProgress && (
        <div className="container-ascendia pb-3" data-testid="property-intelligence-progress">
          <div className="flex justify-between text-[11px] text-muted-c mb-1.5">
            <span aria-live="polite">Step {stepNumber} of {CONFIG_STEPS.length} · {STEP_TITLES[stage]}</span>
            <span className="tabular-nums">{Math.round((stepNumber / CONFIG_STEPS.length) * 100)}%</span>
          </div>
          <div className="h-1 rounded-full bg-white/10 overflow-hidden">
            <motion.div className="h-full bg-[color:var(--gold)]" initial={false} animate={{ width: `${(stepNumber / CONFIG_STEPS.length) * 100}%` }} transition={{ duration: reduce ? 0 : 0.35 }} />
          </div>
        </div>
      )}
    </header>
  );

  // ---------- right profile panel ----------
  const ProfilePanel = ({ className = "" }) => (
    <aside className={className}>
      <div className="ascendia-card p-5 lg:sticky lg:top-28">
        <p className={kicker}>Ascendia Property Profile</p>
        <div className="mt-3">
          <PropertyBlueprint areas={sd.areas} activeArea={activeArea} showMicro={showMicro} hasInput />
        </div>
        <div className="gold-hairline my-4" />
        <PriceRange est={plan?.est} />
        <div className="mt-4"><DurationBand dur={plan?.dur || { low: 0, high: 0, band: "—" }} /></div>
        <div className="mt-4"><ComplexityBadge cx={plan?.cx || { level: "Low" }} /></div>
      </div>
    </aside>
  );

  // ---------- stages ----------
  const IntroStage = () => (
    <section className="relative">
      <div className="hero-glow absolute inset-0 -z-10" />
      <div className="container-ascendia py-16 sm:py-20 min-h-[calc(100vh-3.5rem)] flex flex-col justify-center max-w-3xl">
        <p className={kicker}>Ascendia Property Intelligence™</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white u-tracking-tighter">
          Design your property's <span className="text-gold">detailing plan</span>.
        </h1>
        <p className="mt-5 text-secondary max-w-xl u-body">
          Build a precise profile of your home and watch an architectural plan come to life — with a live estimate, an exact scope, and the detail Ascendia sees that others miss. No photos required.
        </p>
        {resume && (
          <div data-testid="pi-resume-banner" className="mt-8 flex flex-col gap-3 rounded-xl border border-[color:var(--border-strong)] bg-gold/[0.06] p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gold/15 border border-[color:var(--border-subtle)]"><History className="h-4 w-4 text-gold-soft" /></span>
              <div>
                <p className="text-sm font-medium text-white">Continue your property plan</p>
                <p className="text-xs text-muted-c">You have an unfinished plan saved on this device.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={continuePlan} data-testid="pi-resume-continue" className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 h-10 text-sm font-semibold text-black hover:bg-gold-soft transition-colors">Continue <ArrowRight className="h-4 w-4" /></button>
              <button onClick={discardResume} data-testid="pi-resume-discard" className="rounded-lg border border-[color:var(--border-strong)] px-4 h-10 text-sm text-white hover:bg-white/5 transition-colors">Start fresh</button>
            </div>
          </div>
        )}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button onClick={() => goTo("basics")} data-testid="pi-start-button" className="inline-flex items-center gap-2 rounded-xl bg-gold px-6 py-3 text-sm font-semibold text-black hover:bg-gold-soft active:scale-[0.98] transition-transform duration-150">
            Begin your profile <ArrowRight className="h-4 w-4" />
          </button>
          <span className="text-xs text-muted-c">Takes about 90 seconds</span>
        </div>
        <div className="mt-12 grid sm:grid-cols-3 gap-3">
          {[["Live estimate", "A guide range from our real pricing engine."], ["Exact scope", "See what's included at every detail level."], ["What Ascendia sees", "Tracks, hinges, crevices — the overlooked details."]].map(([t, d]) => (
            <div key={t} className="ascendia-card p-4">
              <p className="text-sm font-medium text-white">{t}</p>
              <p className="mt-1 text-xs text-muted-c">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const BasicsStage = () => (
    <div className="space-y-5">
      <div className={cardCls}>
        <h2 className="font-display text-xl font-semibold text-white u-tracking-tight">Tell us about the property</h2>
        <p className="text-sm text-muted-c mt-1">A few essentials so we can shape your plan.</p>
        <div className="mt-5 grid sm:grid-cols-2 gap-5">
          <label className="block">
            <span className="text-sm text-secondary">Property type</span>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {PROPERTY_TYPES.map((t) => (
                <button key={t} type="button" onClick={() => set({ propertyType: t })} data-testid={`pi-type-${t.toLowerCase()}`}
                  className={`rounded-lg border px-2 py-2 text-xs transition-colors duration-150 ${s.propertyType === t ? "border-gold text-gold-soft bg-gold/10" : "border-[color:var(--border-subtle)] text-secondary hover:bg-white/[0.03]"}`}>{t}</button>
              ))}
            </div>
          </label>
          <label className="block">
            <span className="text-sm text-secondary">City / area</span>
            <input className={`${inputCls} mt-2`} value={s.city} onChange={(e) => set({ city: e.target.value })} placeholder="e.g. Tel Aviv" data-testid="pi-city-input" />
          </label>
        </div>
        <div className="mt-5">
          <div className="flex items-end justify-between gap-3">
            <span className="text-sm text-secondary">Interior size</span>
            <div className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border-subtle)] bg-black/30 px-2 py-1 focus-within:border-[color:var(--border-strong)]">
              <input
                type="number" inputMode="numeric" min="10" max="2000" step="1"
                value={s.sqm}
                onChange={(e) => set({ sqm: e.target.value === "" ? "" : Math.max(0, Math.min(2000, Number(e.target.value))) })}
                onBlur={(e) => { if (!Number(e.target.value)) set({ sqm: 20 }); }}
                aria-label="Interior size in square metres"
                data-testid="pi-sqm-input"
                className="w-16 bg-transparent text-right text-gold-soft tabular-nums outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span className="text-sm text-muted-c">m²</span>
            </div>
          </div>
          <input type="range" min="20" max="500" step="5" value={Number(s.sqm) || 20} onChange={(e) => set({ sqm: Number(e.target.value) })} data-testid="pi-sqm-range" className="w-full accent-[#C8A14A] mt-3" />
          <div className="mt-1 flex justify-between text-[10px] text-muted-c"><span>20 m²</span><span>Drag or type an exact size</span><span>500+ m²</span></div>
        </div>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-5 items-end">
          <Stepper label="Rooms" value={s.rooms} onChange={(v) => set({ rooms: v })} min={0} max={15} testid="pi-rooms" />
          <Stepper label="Bathrooms" value={s.bathrooms} onChange={(v) => set({ bathrooms: v })} min={0} max={10} testid="pi-bathrooms" />
          <label className="block col-span-2">
            <span className="text-sm text-secondary">Furnishing</span>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {["Empty", "Furnished"].map((v) => (
                <button key={v} type="button" onClick={() => set({ furnishing: v })} data-testid={`pi-furnishing-${v.toLowerCase()}`}
                  className={`rounded-lg border px-3 py-2 text-sm transition-colors duration-150 ${s.furnishing === v ? "border-gold text-gold-soft bg-gold/10" : "border-[color:var(--border-subtle)] text-secondary hover:bg-white/[0.03]"}`}>{v}</button>
              ))}
            </div>
          </label>
        </div>
      </div>
    </div>
  );

  const NeedConditionStage = () => (
    <div className="space-y-5">
      <div className={cardCls}>
        <h2 className="font-display text-xl font-semibold text-white u-tracking-tight">What does the property need?</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {NEEDS.map((n) => (
            <ChoiceCard key={n.key} active={s.needKey === n.key} title={n.label} desc={n.desc} onClick={() => set({ needKey: n.key })} testid={`pi-need-${n.key}`} />
          ))}
        </div>
      </div>
      <div className={cardCls}>
        <h3 className="font-display text-lg font-semibold text-white">Overall condition</h3>
        <p className="text-sm text-muted-c mt-1">This shapes the effort — and your estimate.</p>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CONDITION_STOPS.map((c) => (
            <button key={c.key} type="button" onClick={() => set({ conditionKey: c.key })} data-testid={`pi-condition-${c.key.toLowerCase()}`}
              className={`text-left rounded-xl border p-3 transition-colors duration-150 ${s.conditionKey === c.key ? "border-gold bg-gold/10" : "border-[color:var(--border-subtle)] hover:bg-white/[0.03]"}`}>
              <span className="block text-sm font-medium text-white">{c.label}</span>
              <span className="mt-1 block text-[11px] text-muted-c">{c.desc}</span>
            </button>
          ))}
        </div>
      </div>
      <div className={cardCls}>
        <h3 className="font-display text-lg font-semibold text-white">A little more detail</h3>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="text-sm text-secondary">Kitchen condition</span>
            <div className="mt-2 flex gap-2">{AREA_CONDITIONS.map((c) => <Chip key={c} active={s.kitchenCondition === c} onClick={() => set({ kitchenCondition: c })} testid={`pi-kitchen-${c.toLowerCase()}`}>{c}</Chip>)}</div>
          </label>
          <label className="block"><span className="text-sm text-secondary">Bathroom condition</span>
            <div className="mt-2 flex gap-2">{AREA_CONDITIONS.map((c) => <Chip key={c} active={s.bathroomCondition === c} onClick={() => set({ bathroomCondition: c })} testid={`pi-bath-${c.toLowerCase()}`}>{c}</Chip>)}</div>
          </label>
          <label className="block"><span className="text-sm text-secondary">Window complexity</span>
            <div className="mt-2 flex flex-wrap gap-2">{WINDOW_COMPLEXITY.map((c) => <Chip key={c} active={s.windowComplexity === c} onClick={() => set({ windowComplexity: c })} testid={`pi-window-${c.replace(/\s/g, "-").toLowerCase()}`}>{c}</Chip>)}</div>
          </label>
          <div className="block"><span className="text-sm text-secondary">Present in the property</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {[["balcony", "Balcony"], ["cabinets", "Cabinets"], ["appliances", "Appliances"], ["constructionResidue", "Construction residue"]].map(([k, l]) => (
                <Chip key={k} active={!!s[k]} onClick={() => set({ [k]: !s[k] })} testid={`pi-has-${k}`}>{s[k] ? <Check className="h-3 w-3" /> : null}{l}</Chip>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className={cardCls}>
        <h3 className="font-display text-lg font-semibold text-white">Timing</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {DEADLINE_PRESETS.map((p) => <Chip key={p} active={s.deadlinePreset === p} onClick={() => set({ deadlinePreset: p })} testid={`pi-deadline-${p.replace(/\s/g, "-").toLowerCase()}`}>{p}</Chip>)}
        </div>
        {s.deadlinePreset === "Specific date" && (
          <input type="date" className={`${inputCls} mt-3 max-w-xs`} value={s.deadlineDate} onChange={(e) => set({ deadlineDate: e.target.value })} data-testid="pi-deadline-date" />
        )}
      </div>
    </div>
  );

  const scopeTiers = (area) => {
    const lvl = s.detailLevel;
    const tiers = [
      { key: "essential", label: "Essential", items: area.scope.essential, on: true },
      { key: "deep", label: "Adds · Deep", items: area.scope.deep, on: lvl === "deep" || lvl === "ascendia" },
      { key: "ascendia", label: "Adds · Ascendia Detail", items: area.scope.ascendia, on: lvl === "ascendia" },
    ];
    return tiers;
  };

  const AreasStage = () => (
    <div className="space-y-5">
      <div className={cardCls}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-xl font-semibold text-white u-tracking-tight">Choose your level of finish</h2>
            <p className="text-sm text-muted-c mt-1">Watch the scope expand as you go deeper.</p>
          </div>
        </div>
        <div className="mt-4"><SegmentedLevels levels={DETAIL_LEVELS} value={s.detailLevel} onChange={setDetail} /></div>
        <p className="mt-3 text-sm text-secondary">{levelByKey(s.detailLevel).blurb}</p>
        <div className="mt-4 flex items-center justify-between rounded-xl border border-[color:var(--border-subtle)] p-3">
          <div className="flex items-center gap-2">
            <Eye className={`h-4 w-4 ${showMicro ? "text-gold-soft" : "text-muted-c"}`} />
            <div>
              <p className="text-sm text-white">What Ascendia sees</p>
              <p className="text-[11px] text-muted-c">Highlight the overlooked details others skip.</p>
            </div>
          </div>
          <button role="switch" aria-checked={showMicro} onClick={() => setShowMicro((v) => !v)} data-testid="what-ascendia-sees-toggle"
            className={`relative h-6 w-11 rounded-full border transition-colors duration-150 ${showMicro ? "bg-gold/30 border-[color:var(--border-strong)]" : "bg-white/5 border-[color:var(--border-subtle)]"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform duration-150 ${showMicro ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {areas.map((area) => {
          const open = openArea === area.id;
          const Icon = area.Icon;
          return (
            <div key={area.id} className={`ascendia-card overflow-hidden ${activeArea === area.id ? "card-accent-top" : ""}`} data-testid={`area-scope-${area.id}`}>
              <button type="button" className="w-full flex items-center gap-3 p-4 text-left"
                onClick={() => { setOpenArea(open ? null : area.id); setActiveArea(area.id); }}
                onMouseEnter={() => setActiveArea(area.id)}>
                <Icon className={`h-5 w-5 ${activeArea === area.id ? "text-gold-soft" : "text-muted-c"}`} />
                <span className="font-medium text-white flex-1">{area.name}</span>
                <ChevronDown className={`h-4 w-4 text-muted-c transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {open && (
                  <motion.div key="body" initial={reduce ? false : { height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: reduce ? 0 : 0.22 }}>
                    <div className="px-4 pb-4 space-y-3">
                      {scopeTiers(area).map((t) => (
                        <div key={t.key} className={t.on ? "" : "opacity-40"}>
                          <p className={`${kicker} ${t.key !== "essential" ? "text-[color:var(--gold-soft)]" : ""}`}>{t.label}{!t.on ? " · locked" : ""}</p>
                          <ul className="mt-1.5 grid sm:grid-cols-2 gap-x-4 gap-y-1">
                            {t.items.map((it, i) => (
                              <li key={`${t.key}-${it}-${i}`} className="flex items-start gap-2 text-sm text-secondary">
                                <Check className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${t.on ? "text-emerald-400" : "text-white/30"}`} />{it}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                      {showMicro && (
                        <div className="pt-1">
                          <p className={kicker}>Ascendia detail points</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {area.micro.map((m, i) => <Chip key={`${m}-${i}`} active dotted testid={`micro-${area.id}-${i}`}>{m}</Chip>)}
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );

  const AddOnsStage = () => (
    <div className="space-y-5">
      <div className={cardCls}>
        <h2 className="font-display text-xl font-semibold text-white u-tracking-tight">Tailor your plan</h2>
        <p className="text-sm text-muted-c mt-1">Add or remove detailing extras — your estimate updates live.</p>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          {(cfg?.addons || []).map((a) => {
            const on = s.addons.includes(a.id);
            return (
              <button key={a.id} type="button" onClick={() => toggleAddon(a.id)} data-testid={`pi-addon-${a.id}`}
                className={`flex items-center justify-between rounded-xl border p-3.5 text-left transition-colors duration-150 ${on ? "border-gold bg-gold/10" : "border-[color:var(--border-subtle)] hover:bg-white/[0.03]"}`}>
                <span className="flex items-center gap-2 text-sm text-white">
                  <span className={`flex h-4 w-4 items-center justify-center rounded border ${on ? "bg-gold border-gold text-black" : "border-white/30"}`}>{on && <Check className="h-3 w-3" />}</span>
                  {a.label}
                </span>
                <span className="text-xs text-gold-soft tabular-nums">+{cfg?.currency}{a.price}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className={cardCls}>
        <label className="block"><span className="text-sm text-secondary">Priority areas or anything specific?</span>
          <textarea rows={2} className={`${inputCls} mt-2`} value={s.priorityAreas} onChange={(e) => set({ priorityAreas: e.target.value })} placeholder="e.g. kitchen and master bathroom are the priority" data-testid="pi-priority-input" />
        </label>
      </div>
    </div>
  );

  const SummaryStage = () => {
    const need = needByKey(s.needKey);
    const rows = [
      ["Property", [s.propertyType, s.city].filter(Boolean).join(" · ")],
      ["Size", `${s.sqm} m²`],
      ["Rooms · Baths", `${s.rooms} · ${s.bathrooms}`],
      ["Furnishing", s.furnishing],
      ["Recommended service", need.label],
      ["Detail level", levelByKey(s.detailLevel).label],
      ["Condition", conditionLabel(s.conditionKey)],
      ["Timing", s.deadlinePreset === "Specific date" ? s.deadlineDate || "Specific date" : s.deadlinePreset],
    ];
    return (
      <div className="space-y-5">
        <div className={cardCls}>
          <p className={kicker}>Ascendia Property Profile</p>
          <h2 className="mt-1 font-display text-2xl font-semibold text-white u-tracking-tight">Here's your tailored plan</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="pi-summary-spec">
            {rows.filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-[color:var(--border-subtle)]/50 py-1.5"><span className="text-muted-c">{k}</span><span className="text-white text-right">{v}</span></div>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className={cardCls}>
            <PriceRange est={plan?.est} />
            {plan?.est?.tightened && <span className="mt-2 inline-flex items-center gap-1 rounded-full border border-[color:var(--border-strong)] px-2.5 py-0.5 text-[11px] text-gold-soft" data-testid="pi-sharpened-badge"><Sparkles className="h-3 w-3" /> Sharpened with your photos</span>}
          </div>
          <div className={cardCls}><DurationBand dur={plan?.dur || { low: 0, high: 0, band: "—" }} /></div>
          <div className={cardCls}><ComplexityBadge cx={plan?.cx || { level: "Low" }} /></div>
        </div>

        {/* Why this range — customer-friendly factors (no formulas/margins exposed) */}
        <div className={cardCls} data-testid="pi-why-range">
          <p className={kicker}>Why this range</p>
          <p className="mt-1 text-sm text-muted-c">The main things shaping your estimate:</p>
          <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {[
              [`${s.sqm} m² to detail`, "Size is the biggest driver of time on site."],
              [`${levelByKey(s.detailLevel).label} detail level`, "A higher finish means more passes and hand-detailing."],
              [`Condition: ${conditionLabel(s.conditionKey)}`, "Heavier build-up takes longer to bring back."],
              [`${s.bathrooms} bathroom${Number(s.bathrooms) === 1 ? "" : "s"}`, "Wet areas are detail-intensive."],
              s.furnishing ? [`${s.furnishing}`, s.furnishing === "Furnished" ? "Working around furniture adds care and time." : "An empty property is quicker to detail."] : null,
              plan?.addonLabels?.length ? [`${plan.addonLabels.length} add-on${plan.addonLabels.length === 1 ? "" : "s"} selected`, "Extras like windows or balcony expand the scope."] : null,
              media.length ? [`${media.length} photo${media.length === 1 ? "" : "s"} added`, "Your photos help us tighten the range."] : null,
            ].filter(Boolean).map(([t, d]) => (
              <li key={t} className="flex items-start gap-2 border-b border-[color:var(--border-subtle)]/40 pb-2">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold-soft" />
                <span><span className="text-white">{t}</span> <span className="text-muted-c">— {d}</span></span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-muted-c">A guide range from our real pricing engine. Final scope and price are confirmed by Ascendia after review.</p>
        </div>
        {/* Photo Boost — optional */}
        <div className={cardCls} data-testid="pi-photo-boost">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className={kicker}>Sharpen your estimate <span className="text-muted-c normal-case tracking-normal">(optional)</span></p>
              <p className="mt-1 text-sm text-secondary">Add a few photos or a short walkthrough and we'll tighten your estimate range — and lock your final quote faster. No photos required.</p>
            </div>
          </div>
          <label className="mt-4 flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[color:var(--border-strong)] bg-black/30 py-6 cursor-pointer hover:bg-white/[0.03] transition-colors duration-150">
            <Upload className="h-5 w-5 text-gold-soft" />
            <span className="text-sm text-secondary">Click to add photos or a walkthrough video</span>
            <input type="file" multiple accept="image/*,video/*" className="hidden" onChange={onUpload} data-testid="pi-photo-input" />
          </label>
          {uploading && <p className="mt-2 text-sm text-muted-c flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</p>}
          {media.length > 0 && (
            <>
              <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="pi-photo-thumbs">
                {media.map((m) => (
                  <div key={m.path} className="relative rounded-lg overflow-hidden border border-[color:var(--border-subtle)] aspect-square bg-black/40">
                    {m.content_type?.startsWith("image") ? <img src={m.preview} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center"><FileVideo className="h-6 w-6 text-gold-soft" /></div>}
                    <button onClick={() => removeMedia(m.path)} aria-label="Remove photo" className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-muted-c">{media.length === 1 ? "Add one more photo to tighten your estimate range." : "Estimate range tightened with your photos — confirmed after review."}</p>
            </>
          )}
        </div>
        {/* Flagship AI vision analysis of uploaded photos */}
        <AiPhotoAnalysisPanel
          media={media}
          context={{ service: needByKey(s.needKey).label, sqm: s.sqm }}
          onApply={applyAi}
          cardCls={cardCls}
          kicker={kicker}
        />
        {plan?.addonLabels?.length > 0 && (
          <div className={cardCls}>
            <p className={kicker}>Included detailing add-ons</p>
            <div className="mt-2 flex flex-wrap gap-2">{plan.addonLabels.map((l, i) => <span key={`${l}-${i}`} className="inline-flex items-center rounded-full border border-[color:var(--border-strong)] px-3 py-1 text-xs text-gold-soft">{l}</span>)}</div>
          </div>
        )}
        {plan?.cx?.factors?.length > 0 && (
          <div className={cardCls}>
            <p className={kicker}>Complexity factors</p>
            <div className="mt-2 flex flex-wrap gap-2">{plan.cx.factors.map((f, i) => <span key={`${f}-${i}`} className="inline-flex items-center rounded-full border border-[color:var(--border-subtle)] px-3 py-1 text-xs text-secondary">{f}</span>)}</div>
          </div>
        )}
        {/* Flagship: room-by-room breakdown + project timeline */}
        <RoomBreakdown areas={areas} levelKey={s.detailLevel} cardCls={cardCls} kicker={kicker} />
        <TimelinePhases dur={plan?.dur} cardCls={cardCls} kicker={kicker} />
        <div className={cardCls}>
          <p className={kicker}>Preparation notes</p>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {PREP_NOTES.map((p) => (
              <div key={p.t} className="flex items-start gap-2"><FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold-soft" /><div><p className="text-sm text-white">{p.t}</p><p className="text-xs text-muted-c">{p.d}</p></div></div>
            ))}
          </div>
        </div>
        <p className="text-[11px] text-muted-c text-center">All pricing and timing are estimates pending a final review of your property.</p>
      </div>
    );
  };

  const ReadyStage = () => (
    <section className="relative">
      <div className="hero-glow absolute inset-0 -z-10" />
      <div className="container-ascendia py-16 sm:py-20 max-w-2xl text-center">
        <motion.div initial={reduce ? false : { opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: reduce ? 0 : 0.4 }} className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-[color:var(--border-strong)] bg-gold/10">
          <Sparkles className="h-6 w-6 text-gold-soft" />
        </motion.div>
        <p className={kicker}>Your plan is ready</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-white u-tracking-tighter">YOUR ASCENDIA PROPERTY PLAN IS READY</h1>
        <p className="mt-4 text-secondary">
          {needByKey(s.needKey).label} · {levelByKey(s.detailLevel).label}
          {plan?.est ? ` · Estimated ${plan.est.currency}${plan.est.low.toLocaleString()}–${plan.est.currency}${plan.est.high.toLocaleString()}` : ""}
        </p>
        <div className="gold-hairline my-8" />
        <div className="grid sm:grid-cols-2 gap-3 text-left">
          <button onClick={getFinalQuote} data-testid="plan-ready-get-final-quote-button" className="inline-flex items-center justify-between gap-2 rounded-xl bg-gold px-5 py-4 text-sm font-semibold text-black hover:bg-gold-soft active:scale-[0.99] transition-transform duration-150">
            Get Final Quote <ArrowRight className="h-4 w-4" />
          </button>
          <button onClick={getFinalQuote} data-testid="plan-ready-request-plan-button" className="inline-flex items-center justify-between gap-2 rounded-xl border border-[color:var(--border-strong)] px-5 py-4 text-sm font-medium text-white hover:bg-white/5 transition-colors duration-150">
            Request This Plan <ClipboardList className="h-4 w-4 text-gold-soft" />
          </button>
          <button onClick={checkAvailability} data-testid="plan-ready-check-availability-button" className="inline-flex items-center justify-between gap-2 rounded-xl border border-[color:var(--border-subtle)] px-5 py-4 text-sm text-white hover:bg-white/5 transition-colors duration-150">
            Check Availability <CalendarClock className="h-4 w-4 text-gold-soft" />
          </button>
          <button onClick={seeEstimate} data-testid="plan-ready-see-estimate-button" className="inline-flex items-center justify-between gap-2 rounded-xl border border-[color:var(--border-subtle)] px-5 py-4 text-sm text-white hover:bg-white/5 transition-colors duration-150">
            Fine-tune Instant Estimate <Calculator className="h-4 w-4 text-gold-soft" />
          </button>
          <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="plan-ready-whatsapp-button" className="inline-flex items-center justify-between gap-2 rounded-xl border border-[color:var(--border-subtle)] px-5 py-4 text-sm text-white hover:bg-white/5 transition-colors duration-150">
            WhatsApp Ascendia <MessageCircle className="h-4 w-4 text-[#25D366]" />
          </a>
          <button onClick={askPlan} data-testid="plan-ready-ask-button" className="inline-flex items-center justify-between gap-2 rounded-xl border border-[color:var(--border-subtle)] px-5 py-4 text-sm text-white hover:bg-white/5 transition-colors duration-150 sm:col-span-2">
            Ask Ascendia About This Plan <Sparkles className="h-4 w-4 text-gold-soft" />
          </button>
          <button onClick={downloadPdf} disabled={downloadingPdf} data-testid="pi-download-pdf-button" className="inline-flex items-center justify-between gap-2 rounded-xl border border-[color:var(--border-strong)] bg-gold/[0.06] px-5 py-4 text-sm font-medium text-white hover:bg-gold/10 disabled:opacity-50 transition-colors duration-150 sm:col-span-2">
            {downloadingPdf ? "Preparing your PDF…" : "Download my property plan (PDF)"} {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 text-gold-soft" />}
          </button>
        </div>

        {/* Save / share this plan */}
        <div className="mt-4 ascendia-card p-5 text-left" data-testid="pi-save-plan">
          <div className="flex items-center gap-2"><Link2 className="h-4 w-4 text-gold-soft" /><p className="text-sm font-medium text-white">Save or share this plan</p></div>
          {!savedLink ? (
            <>
              <p className="mt-1 text-xs text-muted-c">Get a private link to return to this plan later or share it with your household.</p>
              <button onClick={savePlan} disabled={saving} data-testid="pi-save-plan-button" className="mt-3 inline-flex items-center gap-2 rounded-xl border border-[color:var(--border-strong)] px-4 py-2.5 text-sm text-white hover:bg-white/5 disabled:opacity-50 transition-colors duration-150">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-gold-soft" />} Save my plan</button>
            </>
          ) : (
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <input readOnly value={savedLink} data-testid="pi-saved-link" className="flex-1 rounded-lg bg-black/40 border border-[color:var(--border-subtle)] px-3 py-2 text-xs text-white" />
              <div className="flex gap-2">
                <button onClick={copyLink} data-testid="pi-copy-link" className="inline-flex items-center gap-1 rounded-lg border border-[color:var(--border-subtle)] px-3 py-2 text-xs text-gold-soft hover:bg-white/5"><Copy className="h-3.5 w-3.5" /> Copy</button>
                <a href={shareWa} target="_blank" rel="noopener noreferrer" data-testid="pi-share-link" className="inline-flex items-center gap-1 rounded-lg border border-[color:var(--border-subtle)] px-3 py-2 text-xs text-white hover:bg-white/5"><MessageCircle className="h-3.5 w-3.5 text-[#25D366]" /> Share</a>
              </div>
            </div>
          )}
        </div>

        <button onClick={() => goTo("summary")} className="mt-6 inline-flex items-center gap-1 text-xs text-muted-c hover:text-white"><ArrowLeft className="h-3.5 w-3.5" /> Back to my profile</button>
      </div>
      {/* Off-screen branded document for PDF export */}
      <PlanPdfDocument
        ref={pdfRef}
        est={plan?.est}
        dur={plan?.dur}
        cx={plan?.cx}
        addonLabels={plan?.addonLabels || []}
        needLabel={needByKey(s.needKey).label}
        levelLabel={levelByKey(s.detailLevel).label}
        levelKey={s.detailLevel}
        s={s}
        areas={areas}
        aiSummary={aiSummary}
        site={site}
      />
    </section>
  );

  const renderStage = () => {
    // NOTE: these are invoked as functions (not <Stage />) on purpose. Rendering them as
    // JSX elements would give them a new component identity on every keystroke, remounting
    // the inputs and dropping focus. Calling them inlines their output so focus is preserved.
    switch (stage) {
      case "intro": return IntroStage();
      case "basics": return BasicsStage();
      case "need-condition": return NeedConditionStage();
      case "areas": return AreasStage();
      case "add-ons": return AddOnsStage();
      case "summary": return SummaryStage();
      case "ready": return ReadyStage();
      default: return null;
    }
  };

  // ---------- load states ----------
  if (loadState === "loading") {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center text-muted-c" data-testid="estimator-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Calibrating your plan…
      </div>
    );
  }
  if (loadState === "error") {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-4" data-testid="estimator-error">
        <div className="ascendia-card p-8 max-w-md text-center">
          <AlertCircle className="h-9 w-9 text-gold-soft mx-auto" />
          <h1 className="mt-4 font-display text-xl font-semibold text-white">We couldn't load the planner</h1>
          <p className="mt-2 text-sm text-muted-c">Please try again in a moment, or reach us directly and we'll help right away.</p>
          <div className="mt-5 flex justify-center gap-3">
            <button onClick={loadCfg} className="inline-flex items-center gap-2 rounded-xl bg-gold px-5 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft"><RefreshCw className="h-4 w-4" /> Retry</button>
            <a href={`https://wa.me/${site?.phone_intl || "972587790553"}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border-subtle)] px-5 py-2.5 text-sm text-white"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp</a>
          </div>
        </div>
      </div>
    );
  }

  const isImmersive = stage === "intro" || stage === "ready";

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-white noise-overlay pb-28 lg:pb-10">
      <SEO title="Ascendia Property Intelligence" description="Build your property's detailing plan with a live estimate, exact scope and the details Ascendia sees. No photos required." path="/property-intelligence" />
      {TopBar()}

      {/* Keyed enter-fade only (no exit / no mode="wait"): guarantees the new stage
          always mounts immediately. mode="wait" relies on the exit animation's completion
          callback firing — which can stall on iOS Safari and leave a blank screen. */}
      <motion.main
        key={stage}
        initial={reduce ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reduce ? 0 : 0.26 }}
      >
        {isImmersive ? renderStage() : (
          <div className="container-ascendia py-8 lg:py-10 lg:min-h-[calc(100vh-9rem)] lg:flex lg:items-center">
            <div className="w-full grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
              <div className="lg:col-span-7">{renderStage()}</div>
              {showProfilePanel && ProfilePanel({ className: "lg:col-span-5 hidden lg:block" })}
            </div>
          </div>
        )}
      </motion.main>

      {/* Sticky action bar (config stages) */}
      {showProgress && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-[color:var(--border-neutral)] bg-[hsl(var(--background))]/90 backdrop-blur supports-[padding:max(0px)]:pb-[env(safe-area-inset-bottom)]">
          <div className="container-ascendia flex items-center justify-between gap-3 py-3">
            <button onClick={back} data-testid="pi-back-button" className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
            <div className="lg:hidden text-right leading-tight">
              <p className="text-[10px] uppercase tracking-wide text-muted-c">Estimate (guide)</p>
              <p className="text-sm font-semibold text-white tabular-nums">{plan?.est ? `${plan.est.currency}${plan.est.low.toLocaleString()}–${plan.est.currency}${plan.est.high.toLocaleString()}` : "—"}</p>
            </div>
            {stage === "summary" ? (
              <button onClick={() => goTo("ready")} data-testid="pi-generate-plan-button" className="inline-flex items-center gap-2 rounded-xl bg-gold px-5 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft active:scale-[0.98] transition-transform duration-150">Generate my plan <Sparkles className="h-4 w-4" /></button>
            ) : (
              <button onClick={next} data-testid="pi-next-button" className="inline-flex items-center gap-2 rounded-xl bg-gold px-5 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft active:scale-[0.98] transition-transform duration-150">Continue <ArrowRight className="h-4 w-4" /></button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
